import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Heart, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import AOS from 'aos';
import 'aos/dist/aos.css';

const About = () => {
  useEffect(() => {
    AOS.init({
      duration: 600,
      easing: 'ease-in-out',
      once: true
    });
  }, []);

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="display-large" data-aos="fade-up">About James</h1>
          <p className="body-large" data-aos="fade-up" data-aos-delay="100">
            From elite competition to helping you achieve your personal best
          </p>
        </div>
      </section>

      {/* Main Bio Section */}
      <section className="bio-section">
        <div className="container">
          <div className="bio-grid">
            <div className="bio-images" data-aos="fade-right">
              <div className="bio-image-main">
                <img 
                  src="https://customer-assets.emergentagent.com/job_james-workout-zone/artifacts/usdqyc8i_P1118185.jpg" 
                  alt="James Farina Training" 
                />
              </div>
              <div className="bio-image-secondary">
                <img 
                  src="https://customer-assets.emergentagent.com/job_451e4765-98e2-4958-85f3-3894bea3bc6f/artifacts/9yaa1sw7_P1118134.jpg" 
                  alt="James Farina" 
                />
              </div>
            </div>
            
            <div className="bio-content" data-aos="fade-left">
              <h2 className="heading-1">My Journey</h2>
              <p className="body-large">
                Hey, I'm James. I've competed in the Vancity Showdown, trained for marathons, and helped 
                countless people transform their lives. But here's what really matters: I get it. I understand 
                the mental battle, the lifestyle choices, and the dedication it takes to achieve your goals.
              </p>
              
              <p className="body-medium">
                My specialty? Working with busy professionals like you. People who are juggling careers, 
                families, and personal ambitions. You don't need someone to give you a cookie-cutter program. 
                You need a coach who can scale your training to fit your life, not the other way around.
              </p>
              
              <p className="body-medium">
                I bring the same intensity and knowledge from competing at elite levels, but I apply it to 
                real-world scenarios. Whether you're trying to lose weight, build muscle, or train for your 
                first marathon, I'm here to help you reach those goals with programs that actually fit into 
                your busy schedule.
              </p>
              
              <p className="body-medium">
                What sets me apart? I don't just give you workouts and send you on your way. I'm here for 
                the full journey - diet advice, accountability check-ins, and the mental support you need 
                when things get tough. Because let's be honest, fitness isn't just about the physical. It's 
                a mental battle, and I'm in your corner every step of the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="credentials-section">
        <div className="container">
          <h2 className="heading-1 text-center" data-aos="fade-up">My Approach</h2>
          <div className="credentials-grid">
            <div className="credential-card" data-aos="fade-up" data-aos-delay="100">
              <Award size={48} className="credential-icon" />
              <h3 className="heading-3">Elite Experience</h3>
              <p className="body-medium">
                Competed in Vancity Showdown bodybuilding competition and trained for marathons. 
                I bring competition-level expertise to every client.
              </p>
            </div>
            
            <div className="credential-card" data-aos="fade-up" data-aos-delay="200">
              <Heart size={48} className="credential-icon" />
              <h3 className="heading-3">Holistic Support</h3>
              <p className="body-medium">
                Beyond the gym, I provide nutrition advice, accountability, and emotional support. 
                Fitness is a mental battle as much as a physical one.
              </p>
            </div>
            
            <div className="credential-card" data-aos="fade-up" data-aos-delay="300">
              <TrendingUp size={48} className="credential-icon" />
              <h3 className="heading-3">Scalable Programs</h3>
              <p className="body-medium">
                Designed for busy professionals who need efficient, sustainable programs that 
                fit their lifestyle and deliver measurable results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section">
        <div className="container">
          <div className="philosophy-content" data-aos="fade-up">
            <h2 className="heading-1">Training Philosophy</h2>
            <div className="philosophy-text">
              <p className="body-large">
                I believe that sustainable fitness transformation starts with understanding your unique 
                circumstances, goals, and challenges. There's no one-size-fits-all approach.
              </p>
              
              <p className="body-medium">
                Whether you're looking to lose weight, build muscle, prepare for a competition, or simply 
                improve your overall health and mobility, my programs are tailored specifically to you. 
                I focus on:
              </p>
              
              <ul className="philosophy-list">
                <li className="body-medium">
                  <strong>Efficiency:</strong> Maximizing results with the time you have available
                </li>
                <li className="body-medium">
                  <strong>Sustainability:</strong> Building habits that last beyond our training sessions
                </li>
                <li className="body-medium">
                  <strong>Education:</strong> Teaching you the "why" behind every exercise and nutrition choice
                </li>
                <li className="body-medium">
                  <strong>Accountability:</strong> Being your partner in this journey, not just a coach
                </li>
                <li className="body-medium">
                  <strong>Adaptability:</strong> Adjusting your program as your life and goals evolve
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section">
        <div className="container">
          <div className="about-cta-content" data-aos="fade-up">
            <h2 className="display-medium">Let's Work Together</h2>
            <p className="body-large">
              Ready to start your fitness journey with a coach who truly understands your needs?
            </p>
            <Link to="/contact">
              <Button className="btn-cta">
                Book Your Free Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
